/**
 * SitePopoverManager Component
 *
 * Manages popovers for site pins and geofences, rendering content on-demand
 * when a site or geofence is hovered or clicked. Uses a single shared popover
 * for all interactions.
 *
 * Supports two popover types:
 * - Lightweight geofence popover: Shows site name, geofence type info, and "View site details" CTA
 * - Full site popover: Shows complete site stats (reuses SiteStatPopoverContent)
 */

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { WaypointType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';

import { SiteStatPopoverContent } from './SiteStatPopoverContent';
import {
  CircleGeofenceDatum,
  formatGeofenceTag,
  formatRadius,
  formatWaypointType,
  PolygonGeofenceDatum,
  SiteLayerDatum,
} from './utils/transformSiteData';

/**
 * Popover display mode.
 * - 'geofence': Shows lightweight geofence info with "View site details" CTA
 * - 'site': Shows full site details (SiteStatPopoverContent)
 */
type PopoverMode = 'geofence' | 'site';

/**
 * Props for the SitePopoverManager component.
 */
interface SitePopoverManagerProps {
  /** Currently clicked site data (from DeckGL picking) */
  clickedSite: SiteLayerDatum | null;
  /** Currently clicked geofence data */
  clickedGeofence: (CircleGeofenceDatum | PolygonGeofenceDatum) | null;
  /** Whether the clicked geofence is a circle */
  isClickedGeofenceCircle?: boolean;
  /** Screen position for the popover anchor (from DeckGL picking) */
  anchorPosition: { x: number; y: number } | null;
  /** Callback when the popover should close */
  onClose: () => void;
  /** Reference to the map container for positioning */
  mapContainerRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Offset from the anchor position for popover placement (pixels).
 * Negative value moves the popover upward relative to the anchor point.
 */
const POPOVER_OFFSET_Y = -10;

/**
 * Lightweight Geofence Popover Content
 *
 * Shows:
 * - Site name
 * - Geofence type info (Primary: Pickup/Dropoff, Additional: tag)
 * - Radius for circles, "Polygon" for polygons
 * - "View site details" button
 */
const GeofencePopoverContent = ({
  geofence,
  isCircle,
  onViewSiteDetails,
}: {
  geofence: CircleGeofenceDatum | PolygonGeofenceDatum;
  isCircle: boolean;
  onViewSiteDetails: () => void;
}) => {
  const isPrimary = geofence.isPrimary;
  const typeLabel = isPrimary
    ? formatWaypointType(geofence.waypointType ?? 'both')
    : formatGeofenceTag(geofence.tag);

  const shapeLabel = isCircle
    ? `Circle · ${formatRadius((geofence as CircleGeofenceDatum).radiusMeters)}`
    : 'Polygon';

  return (
    <Box sx={{ p: 1.5, maxWidth: '240px' }}>
      <Typography variant="h6" sx={{ fontSize: '14px', fontWeight: 600, mb: 0.5 }} noWrap>
        {geofence.siteName}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
        {typeLabel} · {shapeLabel}
      </Typography>
      <Button
        variant="outlined"
        size="small"
        fullWidth
        onClick={(event) => {
          event.stopPropagation();
          onViewSiteDetails();
        }}
        sx={{ textTransform: 'none' }}
      >
        {t('live_map.geofence_popover.view_site_details', 'View site details')}
      </Button>
    </Box>
  );
};

/**
 * SitePopoverManager manages a single shared popover that displays
 * either geofence info or full site details based on what was clicked.
 *
 * Behavior:
 * 1. When user clicks a geofence: Shows GeofencePopoverContent (lightweight)
 * 2. When user clicks a site pin: Shows SiteStatPopoverContent (full details)
 * 3. When user clicks "View site details" in geofence popover: Transitions to full site view
 * 4. When user clicks outside or presses escape: Closes popover
 *
 * @example
 * ```tsx
 * <SitePopoverManager
 *   clickedSite={clickedSite}
 *   clickedGeofence={clickedGeofence}
 *   isClickedGeofenceCircle={isClickedGeofenceCircle}
 *   anchorPosition={anchorPosition}
 *   onClose={handleClose}
 *   mapContainerRef={mapContainerRef}
 * />
 * ```
 */
export const SitePopoverManager = observer(
  ({
    clickedSite,
    clickedGeofence,
    isClickedGeofenceCircle = true,
    anchorPosition,
    onClose,
    mapContainerRef,
  }: SitePopoverManagerProps) => {
    // Track the current popover mode
    const [mode, setMode] = useState<PopoverMode>('geofence');

    // Store site data for "View site details" transition
    const [viewDetailsSite, setViewDetailsSite] = useState<{
      id: string;
      waypointType: WaypointType | 'both';
      isDefaultSite: boolean;
    } | null>(null);

    // Determine what to show
    const hasContent = clickedSite || clickedGeofence || viewDetailsSite;
    const isOpen = Boolean(hasContent && anchorPosition);

    // Update mode based on what's clicked
    // Order matters: viewDetailsSite takes precedence over clickedGeofence
    // to allow the "View site details" transition to work
    useEffect(() => {
      if (clickedSite) {
        setMode('site');
        setViewDetailsSite(null);
      } else if (viewDetailsSite) {
        // Keep site mode when viewing details from geofence popover
        setMode('site');
      } else if (clickedGeofence) {
        setMode('geofence');
      }
    }, [clickedSite, clickedGeofence, viewDetailsSite]);

    /**
     * Handle clicking "View site details" in geofence popover.
     * Transitions to full site view while keeping popover open.
     */
    const handleViewSiteDetails = useCallback(() => {
      if (!clickedGeofence) return;

      setViewDetailsSite({
        id: clickedGeofence.siteId,
        waypointType: clickedGeofence.waypointType ?? 'both',
        isDefaultSite: clickedGeofence.isDefaultSite ?? false,
      });
      setMode('site');
    }, [clickedGeofence]);

    /**
     * Handle clicking outside the popover (backdrop click).
     * Closes the popover and resets state.
     */
    const handleBackdropClick = useCallback(() => {
      setMode('geofence');
      setViewDetailsSite(null);
      onClose();
    }, [onClose]);

    // Don't render anything if no content to display
    if (!hasContent || !anchorPosition) {
      return null;
    }

    // Create a virtual anchor element at the pick position
    const anchorEl = mapContainerRef?.current ?? null;

    // Determine what content to render
    const renderContent = () => {
      if (mode === 'site' && clickedSite) {
        // Full site details from site pin click
        return (
          <SiteStatPopoverContent
            site={clickedSite.siteData.site}
            type={clickedSite.waypointType}
            isDefaultSite={clickedSite.siteData.isDefaultSite}
          />
        );
      }

      if (mode === 'site' && viewDetailsSite && clickedGeofence) {
        // Full site details from "View site details" button
        // Use the full site data stored in the geofence datum
        return (
          <SiteStatPopoverContent
            site={clickedGeofence.site}
            type={viewDetailsSite.waypointType}
            isDefaultSite={viewDetailsSite.isDefaultSite}
          />
        );
      }

      if (mode === 'geofence' && clickedGeofence) {
        // Lightweight geofence popover
        return (
          <GeofencePopoverContent
            geofence={clickedGeofence}
            isCircle={isClickedGeofenceCircle}
            onViewSiteDetails={handleViewSiteDetails}
          />
        );
      }

      return null;
    };

    return (
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        anchorReference="anchorPosition"
        anchorPosition={{
          top: anchorPosition.y + POPOVER_OFFSET_Y,
          left: anchorPosition.x,
        }}
        onClose={handleBackdropClick}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        disablePortal={false}
        keepMounted={false}
        slotProps={{
          paper: {
            sx: {
              pointerEvents: 'auto',
            },
          },
        }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
      >
        <Box data-test-id="site-popover-content" sx={{ pointerEvents: 'auto' }}>
          {renderContent()}
        </Box>
      </Popover>
    );
  },
);
